import {
  Search,
  Eye,
  Plus,
  Mail,
  Phone,
  DollarSign,
  Trash2,
  X,
  Upload,
  Image as ImageIcon,
} from 'lucide-react'
import { useState } from 'react'

import { Badge } from '../components/ui/badge'
import { Button } from '../components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../components/ui/dialog'
import { Input } from '../components/ui/input'
import { Textarea } from '../components/ui/textarea'

interface Address {
  street: string
  exteriorNumber: string
  interiorNumber: string
  postalCode: string
  neighborhood: string
  municipality: string
  city: string
  state: string
  country: string
  reference: string
}

interface Client {
  id: number
  name: string
  emails: string[]
  phones: string[]
  address: Address
  comments: string
  image?: string
  totalPurchases: number
  lastPurchase: string
  loyaltyPoints: number
}

interface Purchase {
  id: string
  date: string
  total: number
  items: number
}

const clients: Client[] = [
  {
    id: 1,
    name: 'María López García',
    emails: ['maria.lopez@email.com'],
    phones: ['555-1234'],
    address: {
      street: 'Av. Insurgentes',
      exteriorNumber: '123',
      interiorNumber: '4B',
      postalCode: '06700',
      neighborhood: 'Roma Norte',
      municipality: 'Cuauhtémoc',
      city: 'Ciudad de México',
      state: 'CDMX',
      country: 'México',
      reference: 'Frente al parque',
    },
    comments: 'Cliente frecuente, prefiere entregas por la mañana',
    totalPurchases: 15600,
    lastPurchase: '2026-04-25',
    loyaltyPoints: 1560,
  },
  {
    id: 2,
    name: 'Carlos Méndez',
    emails: ['carlos.m@email.com'],
    phones: ['555-5678', '555-9999'],
    address: {
      street: 'Calle Principal',
      exteriorNumber: '45',
      interiorNumber: '',
      postalCode: '03100',
      neighborhood: 'Del Valle',
      municipality: 'Benito Juárez',
      city: 'Ciudad de México',
      state: 'CDMX',
      country: 'México',
      reference: '',
    },
    comments: '',
    totalPurchases: 8900,
    lastPurchase: '2026-04-22',
    loyaltyPoints: 890,
  },
  {
    id: 3,
    name: 'Ana Torres',
    emails: ['ana.torres@email.com', 'ana.t.work@email.com'],
    phones: ['555-9012'],
    address: {
      street: 'Av. Revolución',
      exteriorNumber: '789',
      interiorNumber: '10',
      postalCode: '01030',
      neighborhood: 'San Ángel',
      municipality: 'Álvaro Obregón',
      city: 'Ciudad de México',
      state: 'CDMX',
      country: 'México',
      reference: 'Edificio azul',
    },
    comments: 'Alérgica a ciertos medicamentos',
    totalPurchases: 1250,
    lastPurchase: '2026-04-27',
    loyaltyPoints: 125,
  },
]

const mockPurchases: Purchase[] = [
  { id: 'VT-1245', date: '2026-04-25', total: 450, items: 3 },
  { id: 'VT-1198', date: '2026-04-18', total: 680, items: 5 },
  { id: 'VT-1142', date: '2026-04-10', total: 320, items: 2 },
  { id: 'VT-1089', date: '2026-04-03', total: 890, items: 7 },
]

export function Clients() {
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedClient, setSelectedClient] = useState<Client | null>(null)
  const [showDetailModal, setShowDetailModal] = useState(false)
  const [showNewClientModal, setShowNewClientModal] = useState(false)
  const [newClient, setNewClient] = useState({
    name: '',
    phones: [''],
    emails: [''],
    address: {
      street: '',
      exteriorNumber: '',
      interiorNumber: '',
      postalCode: '',
      neighborhood: '',
      municipality: '',
      city: '',
      state: '',
      country: 'México',
      reference: '',
    },
    comments: '',
    image: null as File | null,
  })

  const filteredClients = clients.filter(
    (client) =>
      client.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      client.emails.some((email) =>
        email.toLowerCase().includes(searchTerm.toLowerCase()),
      ),
  )

  const openDetailModal = (client: Client) => {
    setSelectedClient(client)
    setShowDetailModal(true)
  }

  const addPhone = () => {
    setNewClient({ ...newClient, phones: [...newClient.phones, ''] })
  }

  const removePhone = (index: number) => {
    setNewClient({
      ...newClient,
      phones: newClient.phones.filter((_, i) => i !== index),
    })
  }

  const updatePhone = (index: number, value: string) => {
    const updatedPhones = [...newClient.phones]
    updatedPhones[index] = value
    setNewClient({ ...newClient, phones: updatedPhones })
  }

  const addEmail = () => {
    setNewClient({ ...newClient, emails: [...newClient.emails, ''] })
  }

  const removeEmail = (index: number) => {
    setNewClient({
      ...newClient,
      emails: newClient.emails.filter((_, i) => i !== index),
    })
  }

  const updateEmail = (index: number, value: string) => {
    const updatedEmails = [...newClient.emails]
    updatedEmails[index] = value
    setNewClient({ ...newClient, emails: updatedEmails })
  }

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      setNewClient({ ...newClient, image: file })
    }
  }

  const handleSaveClient = () => {
    console.log('Guardando cliente:', newClient)
    setShowNewClientModal(false)
    // Reset form
    setNewClient({
      name: '',
      phones: [''],
      emails: [''],
      address: {
        street: '',
        exteriorNumber: '',
        interiorNumber: '',
        postalCode: '',
        neighborhood: '',
        municipality: '',
        city: '',
        state: '',
        country: 'México',
        reference: '',
      },
      comments: '',
      image: null,
    })
  }

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">
            Clientes (CRM)
          </h1>
          <p className="mt-1 text-sm text-slate-600">
            {filteredClients.length} clientes registrados
          </p>
        </div>
        <Button onClick={() => setShowNewClientModal(true)}>
          <Plus className="mr-2 h-4 w-4" />
          Nuevo Cliente
        </Button>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <div className="rounded-lg border border-slate-200 bg-white p-6">
          <div className="flex items-center gap-3">
            <div className="rounded-lg bg-primary/10 p-3">
              <DollarSign className="h-6 w-6 text-primary" />
            </div>
            <div>
              <p className="text-sm text-slate-600">Total Clientes</p>
              <p className="text-2xl font-semibold text-slate-900">
                {clients.length}
              </p>
            </div>
          </div>
        </div>
        <div className="rounded-lg border border-slate-200 bg-white p-6">
          <div className="flex items-center gap-3">
            <div className="rounded-lg bg-green-50 p-3">
              <DollarSign className="h-6 w-6 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-slate-600">Compras Totales</p>
              <p className="text-2xl font-semibold text-slate-900">
                $
                {clients
                  .reduce((sum, c) => sum + c.totalPurchases, 0)
                  .toLocaleString()}
              </p>
            </div>
          </div>
        </div>
        <div className="rounded-lg border border-slate-200 bg-white p-6">
          <div className="flex items-center gap-3">
            <div className="rounded-lg bg-purple-50 p-3">
              <DollarSign className="h-6 w-6 text-purple-600" />
            </div>
            <div>
              <p className="text-sm text-slate-600">Puntos Totales</p>
              <p className="text-2xl font-semibold text-slate-900">
                {clients
                  .reduce((sum, c) => sum + c.loyaltyPoints, 0)
                  .toLocaleString()}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="rounded-lg border border-slate-200 bg-white p-4">
        <div className="relative">
          <Search className="absolute top-1/2 left-3 h-5 w-5 -translate-y-1/2 text-slate-400" />
          <Input
            type="text"
            placeholder="Buscar cliente por nombre o email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
      </div>

      {/* Clients Table */}
      <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
        <table className="w-full">
          <thead className="border-b border-slate-200 bg-slate-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase">
                ID
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase">
                Cliente
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase">
                Contacto
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase">
                Compras Totales
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase">
                Puntos
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase">
                Última Compra
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase">
                Acciones
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200">
            {filteredClients.map((client) => (
              <tr key={client.id} className="hover:bg-slate-50">
                <td className="px-6 py-4">
                  <span className="font-mono text-sm text-slate-600">
                    {client.id}
                  </span>
                </td>
                <td className="px-6 py-4">
                  <div className="font-medium text-slate-900">
                    {client.name}
                  </div>
                </td>
                <td className="px-6 py-4">
                  <div className="flex flex-col gap-1">
                    {client.emails[0] && (
                      <div className="flex items-center gap-2 text-sm text-slate-600">
                        <Mail className="h-3 w-3" />
                        {client.emails[0]}
                        {client.emails.length > 1 && (
                          <Badge variant="outline" className="text-xs">
                            +{client.emails.length - 1}
                          </Badge>
                        )}
                      </div>
                    )}
                    {client.phones[0] && (
                      <div className="flex items-center gap-2 text-sm text-slate-600">
                        <Phone className="h-3 w-3" />
                        {client.phones[0]}
                        {client.phones.length > 1 && (
                          <Badge variant="outline" className="text-xs">
                            +{client.phones.length - 1}
                          </Badge>
                        )}
                      </div>
                    )}
                  </div>
                </td>
                <td className="px-6 py-4 font-semibold text-green-600">
                  ${client.totalPurchases.toLocaleString()}
                </td>
                <td className="px-6 py-4 font-medium text-slate-900">
                  {client.loyaltyPoints}
                </td>
                <td className="px-6 py-4 text-slate-700">
                  {client.lastPurchase}
                </td>
                <td className="px-6 py-4">
                  <button
                    onClick={() => openDetailModal(client)}
                    className="rounded p-1 hover:bg-slate-100"
                  >
                    <Eye className="h-4 w-4 text-slate-600" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Client Detail Modal */}
      <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>Perfil del Cliente</DialogTitle>
            <DialogDescription className="sr-only">
              Información detallada del cliente y su historial de compras
            </DialogDescription>
          </DialogHeader>
          {selectedClient && (
            <div className="space-y-6">
              {/* Client Info */}
              <div className="rounded-lg bg-slate-50 p-6">
                <h3 className="mb-4 text-lg font-semibold text-slate-900">
                  {selectedClient.name}
                </h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-slate-600">ID:</span>
                    <p className="font-medium text-slate-900">
                      {selectedClient.id}
                    </p>
                  </div>
                  <div>
                    <span className="text-slate-600">Puntos de Lealtad:</span>
                    <p className="font-medium text-slate-900">
                      {selectedClient.loyaltyPoints}
                    </p>
                  </div>
                  <div className="col-span-2">
                    <span className="text-slate-600">Emails:</span>
                    <div className="mt-1 space-y-1">
                      {selectedClient.emails.map((email, idx) => (
                        <p key={idx} className="font-medium text-slate-900">
                          {email}
                        </p>
                      ))}
                    </div>
                  </div>
                  <div className="col-span-2">
                    <span className="text-slate-600">Teléfonos:</span>
                    <div className="mt-1 space-y-1">
                      {selectedClient.phones.map((phone, idx) => (
                        <p key={idx} className="font-medium text-slate-900">
                          {phone}
                        </p>
                      ))}
                    </div>
                  </div>
                  <div className="col-span-2">
                    <span className="text-slate-600">Dirección:</span>
                    <p className="mt-1 font-medium text-slate-900">
                      {selectedClient.address.street}{' '}
                      {selectedClient.address.exteriorNumber}
                      {selectedClient.address.interiorNumber &&
                        ` Int. ${selectedClient.address.interiorNumber}`}
                      , {selectedClient.address.neighborhood},{' '}
                      {selectedClient.address.city},{' '}
                      {selectedClient.address.state}, CP{' '}
                      {selectedClient.address.postalCode}
                    </p>
                  </div>
                  {selectedClient.comments && (
                    <div className="col-span-2">
                      <span className="text-slate-600">Comentarios:</span>
                      <p className="mt-1 font-medium text-slate-900">
                        {selectedClient.comments}
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Metrics */}
              <div className="grid grid-cols-3 gap-4">
                <div className="rounded-lg bg-green-50 p-4">
                  <p className="text-sm text-green-700">Compras Totales</p>
                  <p className="text-xl font-semibold text-green-900">
                    ${selectedClient.totalPurchases.toLocaleString()}
                  </p>
                </div>
                <div className="rounded-lg bg-primary/10 p-4">
                  <p className="text-sm text-primary">Última Compra</p>
                  <p className="text-xl font-semibold text-primary/90">
                    {selectedClient.lastPurchase}
                  </p>
                </div>
                <div className="rounded-lg bg-purple-50 p-4">
                  <p className="text-sm text-purple-700">Ticket Promedio</p>
                  <p className="text-xl font-semibold text-purple-900">$450</p>
                </div>
              </div>

              {/* Purchase History */}
              <div>
                <h4 className="mb-3 font-medium text-slate-900">
                  Historial de Compras
                </h4>
                <div className="overflow-hidden rounded-lg border border-slate-200">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="px-4 py-2 text-left text-xs font-medium text-slate-700">
                          Ticket
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-slate-700">
                          Fecha
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-slate-700">
                          Items
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-slate-700">
                          Total
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200">
                      {mockPurchases.map((purchase) => (
                        <tr key={purchase.id} className="hover:bg-slate-50">
                          <td className="px-4 py-2 font-medium">
                            {purchase.id}
                          </td>
                          <td className="px-4 py-2">{purchase.date}</td>
                          <td className="px-4 py-2">{purchase.items}</td>
                          <td className="px-4 py-2 font-semibold text-green-600">
                            ${purchase.total}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* New Client Modal */}
      <Dialog open={showNewClientModal} onOpenChange={setShowNewClientModal}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-4xl">
          <DialogHeader>
            <DialogTitle>Nuevo Cliente</DialogTitle>
            <DialogDescription className="sr-only">
              Formulario para registrar un nuevo cliente
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-6">
            {/* ID and Name */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700">
                  ID <span className="text-red-600">*</span>
                </label>
                <Input
                  value={clients.length + 1}
                  disabled
                  className="bg-slate-100"
                />
                <p className="text-xs text-slate-500">ID auto-generado</p>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700">
                  Nombre Completo <span className="text-red-600">*</span>
                </label>
                <Input
                  value={newClient.name}
                  onChange={(e) =>
                    setNewClient({ ...newClient, name: e.target.value })
                  }
                  placeholder="Ej: Juan Pérez García"
                />
              </div>
            </div>

            {/* Phones */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium text-slate-700">
                  Teléfonos <span className="text-red-600">*</span>
                </label>
                <Button size="sm" variant="outline" onClick={addPhone}>
                  <Plus className="mr-1 h-4 w-4" />
                  Agregar Teléfono
                </Button>
              </div>
              <div className="space-y-2">
                {newClient.phones.map((phone, index) => (
                  <div key={index} className="flex gap-2">
                    <Input
                      value={phone}
                      onChange={(e) => updatePhone(index, e.target.value)}
                      placeholder="555-1234"
                    />
                    {newClient.phones.length > 1 && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => removePhone(index)}
                        className="text-red-600 hover:text-red-700"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Emails */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium text-slate-700">
                  Correos Electrónicos <span className="text-red-600">*</span>
                </label>
                <Button size="sm" variant="outline" onClick={addEmail}>
                  <Plus className="mr-1 h-4 w-4" />
                  Agregar Email
                </Button>
              </div>
              <div className="space-y-2">
                {newClient.emails.map((email, index) => (
                  <div key={index} className="flex gap-2">
                    <Input
                      type="email"
                      value={email}
                      onChange={(e) => updateEmail(index, e.target.value)}
                      placeholder="correo@ejemplo.com"
                    />
                    {newClient.emails.length > 1 && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => removeEmail(index)}
                        className="text-red-600 hover:text-red-700"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Address */}
            <div className="space-y-3 rounded-lg bg-slate-50 p-4">
              <h4 className="font-medium text-slate-900">Dirección</h4>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                <div className="space-y-2 md:col-span-2">
                  <label className="text-sm font-medium text-slate-700">
                    Calle
                  </label>
                  <Input
                    value={newClient.address.street}
                    onChange={(e) =>
                      setNewClient({
                        ...newClient,
                        address: {
                          ...newClient.address,
                          street: e.target.value,
                        },
                      })
                    }
                    placeholder="Av. Insurgentes"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-700">
                    Número Exterior
                  </label>
                  <Input
                    value={newClient.address.exteriorNumber}
                    onChange={(e) =>
                      setNewClient({
                        ...newClient,
                        address: {
                          ...newClient.address,
                          exteriorNumber: e.target.value,
                        },
                      })
                    }
                    placeholder="123"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-700">
                    Número Interior
                  </label>
                  <Input
                    value={newClient.address.interiorNumber}
                    onChange={(e) =>
                      setNewClient({
                        ...newClient,
                        address: {
                          ...newClient.address,
                          interiorNumber: e.target.value,
                        },
                      })
                    }
                    placeholder="4B (opcional)"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-700">
                    Código Postal
                  </label>
                  <Input
                    value={newClient.address.postalCode}
                    onChange={(e) =>
                      setNewClient({
                        ...newClient,
                        address: {
                          ...newClient.address,
                          postalCode: e.target.value,
                        },
                      })
                    }
                    placeholder="06700"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-700">
                    Colonia
                  </label>
                  <Input
                    value={newClient.address.neighborhood}
                    onChange={(e) =>
                      setNewClient({
                        ...newClient,
                        address: {
                          ...newClient.address,
                          neighborhood: e.target.value,
                        },
                      })
                    }
                    placeholder="Roma Norte"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-700">
                    Municipio
                  </label>
                  <Input
                    value={newClient.address.municipality}
                    onChange={(e) =>
                      setNewClient({
                        ...newClient,
                        address: {
                          ...newClient.address,
                          municipality: e.target.value,
                        },
                      })
                    }
                    placeholder="Cuauhtémoc"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-700">
                    Ciudad
                  </label>
                  <Input
                    value={newClient.address.city}
                    onChange={(e) =>
                      setNewClient({
                        ...newClient,
                        address: { ...newClient.address, city: e.target.value },
                      })
                    }
                    placeholder="Ciudad de México"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-700">
                    Estado
                  </label>
                  <Input
                    value={newClient.address.state}
                    onChange={(e) =>
                      setNewClient({
                        ...newClient,
                        address: {
                          ...newClient.address,
                          state: e.target.value,
                        },
                      })
                    }
                    placeholder="CDMX"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-700">
                    País
                  </label>
                  <Input
                    value={newClient.address.country}
                    onChange={(e) =>
                      setNewClient({
                        ...newClient,
                        address: {
                          ...newClient.address,
                          country: e.target.value,
                        },
                      })
                    }
                    placeholder="México"
                  />
                </div>
                <div className="space-y-2 md:col-span-3">
                  <label className="text-sm font-medium text-slate-700">
                    Referencia
                  </label>
                  <Input
                    value={newClient.address.reference}
                    onChange={(e) =>
                      setNewClient({
                        ...newClient,
                        address: {
                          ...newClient.address,
                          reference: e.target.value,
                        },
                      })
                    }
                    placeholder="Frente al parque, edificio azul..."
                  />
                </div>
              </div>
            </div>

            {/* Comments */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-700">
                Comentarios
              </label>
              <Textarea
                value={newClient.comments}
                onChange={(e) =>
                  setNewClient({ ...newClient, comments: e.target.value })
                }
                placeholder="Notas adicionales sobre el cliente..."
                rows={3}
              />
            </div>

            {/* Image Upload */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-700">
                Imagen del Cliente
              </label>
              <div className="rounded-lg border-2 border-dashed border-slate-200 p-6 text-center">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  className="hidden"
                  id="client-image"
                />
                <label htmlFor="client-image" className="cursor-pointer">
                  <ImageIcon className="mx-auto mb-2 h-12 w-12 text-slate-400" />
                  <p className="text-sm text-slate-600">
                    {newClient.image
                      ? newClient.image.name
                      : 'Haz clic para subir imagen'}
                  </p>
                  <p className="mt-1 text-xs text-slate-500">
                    PNG, JPG hasta 5MB
                  </p>
                </label>
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-3 border-t border-slate-200 pt-4">
              <Button
                onClick={handleSaveClient}
                disabled={
                  !newClient.name ||
                  newClient.phones.some((p) => !p) ||
                  newClient.emails.some((e) => !e)
                }
                className="flex-1"
              >
                Guardar Cliente
              </Button>
              <Button
                variant="outline"
                onClick={() => setShowNewClientModal(false)}
                className="flex-1"
              >
                <X className="mr-2 h-4 w-4" />
                Cancelar
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}
